<template>
  <b-card no-body>
    <b-card-body>
      <product-list-filter
        :values="filterValues"
        :filter-submitting="isLoading"
        @filter="setFilter"
        @open-columns="openCustomColumnsModal"
      />

      <vue-good-table
        mode="remote"
        :sort-options="{ enabled: false }"
        :total-rows="totalRecords"
        :is-loading.sync="isLoading"
        :show-footer="true"
        :pagination-options="{
          enabled: true,
          jumpFirstOrLast: true,
          rowsPerPageLabel: t('Rows per page'),
          ofLabel: t('ofLabel'),
          nextLabel: t('nextLabel'),
          prevLabel: t('prevLabel'),
          dropdownAllowAll: false
        }"
        :rows="rows"
        :columns="getColumns"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'description'">
            {{ props.row.description || '-' }}
          </span>
          <span v-else-if="props.column.field === 'merchant_product_id'">
            {{ props.row.merchant_product_id || '-' }}
          </span>
          <span v-else-if="props.column.field === 'manufacturer_product_id'">
            {{ props.row.manufacturer_product_id || '-' }}
          </span>
          <span v-else-if="props.column.field === 'standard_product_id'">
            {{ props.row.standard_product_id || '-' }}
          </span>
          <span v-else-if="props.column.field === 'gtip'">
            {{ props.row.gtip.code }}
          </span>
          <span v-else-if="props.column.field === 'originCountry'">
            {{ props.row.originCountry.name }}
          </span>
          <span v-else-if="props.column.field === 'currency'">
            {{ getCurrencyCode(props.row.currency.code) }}
          </span>

          <span v-else-if="props.column.field === 'actions'">
            <span class="d-flex justify-content-center table-icon-buttons">
              <b-button
                :id="`edit-button-${props.row.id}`"
                variant="primary"
                type="button"
                size="sm"
                @click="editProduct(props.row.id)"
              >
                <feather-icon icon="Edit2Icon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`edit-button-${props.row.id}`"
                  :title="$t('Edit Product')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>

              <b-button
                :id="`delete-button-${props.row.id}`"
                variant="danger"
                type="button"
                size="sm"
                @click="deleteProduct(props.row.id)"
              >
                <feather-icon icon="XIcon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`delete-button-${props.row.id}`"
                  :title="$t('Delete Product')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
            </span>
          </span>
        </template>
      </vue-good-table>

      <CustomColumnsModal
        ref="customColumnsModal"
        v-model="visibleColumnKeys"
        :options="columnToggleOptions"
        @save="saveCustomColumns"
      />
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardBody, BTooltip, BButton } from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { mapGetters } from 'vuex';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import ProductListFilter from '@/views/pages/products/ProductListFilter.vue';
import CustomColumnsModal from '@/views/pages/consignments/CustomColumnsModal.vue';
import useService from '@/auth/service/useService';
import store from '@/store/index';

export default {
  components: {
    VueGoodTable,
    BCard,
    BCardBody,
    BTooltip,
    BButton,
    CustomColumnsModal,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    ProductListFilter
  },
  mixins: [helper],
  data() {
    return {
      isLoading: false,
      rows: [],
      details: [],
      totalRecords: 0,
      filter: {
        page: 1,
        limit: 10
      },
      visibleColumnKeys: ['description', 'gtip', 'originCountry.name', 'currency'],
      filterValues: {
        description: '',
        gtip: '',
        origin: '',
        currency_code: ''
      },
      defaultFilterValues: {
        description: '',
        gtip: '',
        origin: '',
        currency_code: ''
      },
      refundData: null,
      refundSubmitting: false,
      refundPrice: '',
      refundAllPriceCheckbox: false
    };
  },
  computed: {
    ...mapGetters({}),
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true;
        return this.dir;
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false;
      return this.dir;
    },
    columnToggleOptions() {
      return [
        { text: this.$t('Product Name'), value: 'description', icon: 'TagIcon' },
        { text: this.$t('Merchant Product ID'), value: 'merchant_product_id', icon: 'HashIcon' },
        {
          text: this.$t('Manufacturer Product ID'),
          value: 'manufacturer_product_id',
          icon: 'BoxIcon'
        },
        {
          text: this.$t('Standard Product ID'),
          value: 'standard_product_id',
          icon: 'BarChart2Icon'
        },
        { text: this.$t('Gtip Code'), value: 'gtip', icon: 'FileTextIcon' },
        { text: this.$t('Origin'), value: 'originCountry.name', icon: 'GlobeIcon' },
        { text: this.$t('Currency Code'), value: 'currency', icon: 'DollarSignIcon' }
      ];
    },
    getColumns() {
      if (!this.rows.length) {
        return [];
      }

      const baseColumns = [
        {
          label: this.$t('Product Name'),
          field: 'description'
        },
        {
          label: this.$t('Merchant Product ID'),
          field: 'merchant_product_id'
        },
        {
          label: this.$t('Manufacturer Product ID'),
          field: 'manufacturer_product_id'
        },
        {
          label: this.$t('Standard Product ID'),
          field: 'standard_product_id'
        },
        {
          label: this.$t('Gtip Code'),
          field: 'gtip'
        },
        {
          label: this.$t('Origin'),
          field: 'originCountry.name'
        },
        {
          label: this.$t('Currency Code'),
          field: 'currency'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];

      return baseColumns.filter(
        c => c.field === 'actions' || this.visibleColumnKeys.includes(c.field)
      );
    }
  },
  watch: {
    visibleColumnKeys: {
      handler(newKeys) {
        try {
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem('products-visible-columns', JSON.stringify(newKeys || []));
          }
        } catch (e) {
          // Ignore localStorage errors
        }
      },
      deep: false
    }
  },
  async created() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const savedColumns = window.localStorage.getItem('products-visible-columns');
        if (savedColumns) {
          const parsed = JSON.parse(savedColumns);
          if (Array.isArray(parsed) && parsed.length) {
            this.visibleColumnKeys = parsed;
          }
        }
      }
    } catch (e) {
      // ignore JSON or storage errors
    }
    this.loadItems();
  },
  methods: {
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    openCustomColumnsModal() {
      this.$refs.customColumnsModal.show();
    },

    saveCustomColumns(newColumns) {
      this.visibleColumnKeys = newColumns;
    },

    // load items is what brings back the rows from server
    loadItems() {
      let requestData = this.filter;

      requestData = {
        ...requestData,
        ...this.getFilteredObject(this.filterValues)
      };

      if (
        requestData.dateFrom &&
        requestData.dateTo &&
        new Date(requestData.dateFrom) > new Date(requestData.dateTo)
      ) {
        this.showErrorMessage(this.$t('Big Date Error'), this.$t('Unsuccessful'));
        return false;
      }

      this.isLoading = true;
      useService
        .getAllProducts(requestData)
        .then(response => {
          this.rows = response.data;
          this.totalRecords = response.meta.total;
        })
        .finally(() => {
          this.isLoading = false;
        });

      return true;
    },
    setFilter(data) {
      this.filterValues = data
        ? { ...this.defaultFilterValues, ...data }
        : { ...this.defaultFilterValues };
      this.filter.page = 1;
      this.loadItems();
    },
    editProduct(id) {
      this.$router.push(`/products/${id}`);
    },
    deleteProduct(id) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Delete Product Warning')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          await useService.deleteProduct(id).then(response => {
            this.showSuccessMessage(
              `${response.message || this.$t('Delete Product Success Message')}`
            );

            const index = this.rows.findIndex(item => item.id === id);
            this.rows.splice(index, 1);
          });
        }
      });
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
